import React, { useMemo, useState } from 'react'
import { CSmartTable } from '@coreui/react-pro'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import FixtureVotingService from 'src/service/FixtureVotingService'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import LoadError from './LoadError'
import VoteDetailModal from './VoteDetailModal'

const SEARCH_FIELDS = [{ key: 'team_name', label: 'Team' }]

const columns = [
  { key: 'team_name', label: 'Team', filter: false },
  { key: 'start_date', label: 'Fixture Date', filter: false, _style: { width: '28%' } },
  { key: 'actions', label: '', filter: false, sorter: false, _style: { width: '8%' } },
]

const DETAILS = {
  breakdown: {
    title: 'Vote Breakdown',
    request: FixtureVotingService.getFixtureVote,
    columns: [
      { key: 'full_name', label: 'Player' },
      { key: 'three_votes', label: '3 Votes', numeric: true },
      { key: 'two_votes', label: '2 Votes', numeric: true },
      { key: 'one_votes', label: '1 Vote', numeric: true },
      { key: 'total_votes', label: 'Total', numeric: true },
    ],
  },
  average: {
    title: 'Average Votes',
    request: FixtureVotingService.getAverageFitureVote,
    columns: [
      { key: 'full_name', label: 'Player' },
      { key: 'total_votes', label: 'Votes', numeric: true },
      { key: 'avg_votes', label: 'Avg. Votes', numeric: true },
    ],
  },
}

export const fixtureDates = (fixture) => {
  const start = moment(fixture.start_date)
  const end = moment(fixture.end_date)
  if (!end.isValid() || end.isSame(start, 'day')) return start.format('MMM D, YYYY')
  return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
}

const FixtureVotesTab = ({ fixtures, loading, error, onRetry }) => {
  const navigate = useNavigate()
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [detail, setDetail] = useState(null)

  const items = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return fixtures
    return fixtures.filter((row) =>
      String(row[searchField] ?? '')
        .toLowerCase()
        .includes(term),
    )
  }, [fixtures, searchField, searchValue])

  const config = detail && DETAILS[detail.type]
  const load = useMemo(
    () => detail && (() => DETAILS[detail.type].request(detail.fixture.fixture_id)),
    [detail],
  )

  if (error) return <LoadError message="We couldn't load the fixtures." onRetry={onRetry} />

  return (
    <>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={(field) => {
          setSearchField(field)
          setSearchValue('')
        }}
        value={searchValue}
        onSearch={setSearchValue}
        placeholder="Search fixtures..."
      />

      <div className="ge-datatable ge-datatable--no-select">
        <CSmartTable
          columns={columns}
          items={items}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          noItemsLabel={
            searchValue.trim()
              ? 'No fixtures match your search'
              : 'No votes yet - fixtures appear here once members start voting'
          }
          pagination
          columnSorter
          scopedColumns={{
            team_name: (item) => <td className="vt-player">{item.team_name || '—'}</td>,
            start_date: (item) => <td className="vt-muted">{fixtureDates(item)}</td>,
            actions: (item) => (
              <td className="text-center">
                <RowActionsMenu
                  actions={[
                    {
                      label: 'Vote Breakdown',
                      onClick: () => setDetail({ type: 'breakdown', fixture: item }),
                    },
                    {
                      label: 'Average Votes',
                      onClick: () => setDetail({ type: 'average', fixture: item }),
                    },
                    {
                      label: 'Votes by Member',
                      onClick: () =>
                        navigate(`/votes/fixture/${item.fixture_id}/members`, {
                          state: { teamName: item.team_name, dates: fixtureDates(item) },
                        }),
                    },
                  ]}
                />
              </td>
            ),
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      <VoteDetailModal
        load={load}
        title={config?.title}
        subtitle={detail && `${detail.fixture.team_name} · ${fixtureDates(detail.fixture)}`}
        columns={config?.columns || []}
        emptyLabel="No players have been voted for in this fixture."
        onClose={() => setDetail(null)}
      />
    </>
  )
}

export default FixtureVotesTab
